import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useSnapshotFeedback } from "@/lib/api/care";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { Mail, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { CareButton, CareCard, FieldLabel } from "./care-primitives";

interface AboutThisRecordProps {
  menteeId: string;
  advisor: { name: string; email: string };
}

export function AboutThisRecord({ menteeId, advisor }: AboutThisRecordProps) {
  return (
    <CareCard className="flex flex-col overflow-hidden rounded-2xl p-0 pb-1">
      <h2 className="bg-[rgba(14,13,12,0.08)] px-5 py-4 font-body text-xs font-bold leading-4 text-[#1f1e1d]">
        About this Record
      </h2>
      <div className="flex flex-col gap-1 border-b border-[#cbc6be] px-5 py-4">
        <FieldLabel className="inline-flex items-center gap-1.5 text-[#a0998d]">
          <Mail className="size-3" aria-hidden />
          Clinical Advisor
        </FieldLabel>
        <p className="font-body text-sm leading-5 text-black">
          {advisor.name} is the final decision-maker on all clinical notes and
          determinations on this page.{" "}
          <a
            href={`mailto:${advisor.email}`}
            className="inline-flex items-center gap-1 text-states-hyperlinked underline"
          >
            <Mail className="size-3" aria-hidden />
            Email
          </a>
        </p>
      </div>
      <div className="flex flex-col gap-1 border-b border-[#cbc6be] px-5 py-4">
        <FieldLabel className="inline-flex items-center gap-1.5 text-[#a0998d]">
          <Sparkles className="size-3" aria-hidden />
          AI Usage
        </FieldLabel>
        <p className="font-body text-sm leading-5 text-black">
          AI is used in part on this page. Always review AI-generated content
          for accuracy.
        </p>
      </div>
      <div className="px-5 py-4">
        <AiFeedback menteeId={menteeId} />
      </div>
    </CareCard>
  );
}

function AiFeedback({ menteeId }: { menteeId: string }) {
  const { toast } = useToast();
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const feedback = useSnapshotFeedback(menteeId);

  if (done) {
    return (
      <p className="font-body text-sm leading-5 text-[#58534b]">
        Thanks for the feedback.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <FieldLabel className="text-[#a0998d]">Was this helpful?</FieldLabel>
        <ThumbButton
          label="Helpful"
          active={helpful === true}
          onClick={() => setHelpful(true)}
        >
          <ThumbsUp
            className="size-4"
            fill={helpful === true ? "currentColor" : "none"}
          />
        </ThumbButton>
        <ThumbButton
          label="Not helpful"
          active={helpful === false}
          onClick={() => setHelpful(false)}
        >
          <ThumbsDown
            className="size-4"
            fill={helpful === false ? "currentColor" : "none"}
          />
        </ThumbButton>
      </div>
      {helpful !== null ? (
        <>
          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={3}
            placeholder="What was good or bad about the AI-generated content?"
            className="rounded-xl border-[#8a8275] px-6 py-4 font-body text-sm leading-5 text-black placeholder:text-[#a0998d]"
          />
          <CareButton
            variant="outline"
            className="self-start"
            disabled={feedback.isPending}
            onClick={() =>
              feedback.mutate(
                { helpful, comment: comment.trim() || undefined },
                {
                  onSuccess: () => {
                    trackEvent(PostHogEvents.CARE_PAGE_AI_FEEDBACK_SUBMITTED, {
                      mentee_id: menteeId,
                      rating: helpful ? "thumbs_up" : "thumbs_down",
                      has_comment: comment.trim().length > 0,
                    });
                    setDone(true);
                  },
                  onError: () =>
                    toast({
                      title: "Could not send feedback",
                      variant: "destructive",
                    }),
                },
              )
            }
          >
            Submit
          </CareButton>
        </>
      ) : null}
    </div>
  );
}

function ThumbButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex size-6 items-center justify-center rounded-[7.2px] text-[#1f1e1d]",
        active && "bg-[rgba(0,0,0,0.04)]",
      )}
    >
      {children}
    </button>
  );
}
